import React, { FC, useMemo } from 'react'
import styles from 'styles/components/list.module.scss'
import { AccordionItem, RevenueAccordion } from 'components/Accordion'
import { RevenueType } from 'types/revenue'
import { LightTag } from 'components/Tag'
import { Spacer } from 'components/Utility'
import { FlexRow } from 'components/Flex'
import { CheckoutItemType } from 'types/checkoutItem'

type Props = {
  expandStatus: string
  resetExpandedStatus: () => void
  revenue: RevenueType
}

const RevenueListItem: FC<Props> = (props) => {
  const { expandStatus, resetExpandedStatus, revenue } = props

  // 袋名ごとの購入枚数を集計する。quantityが0の袋（購入されていない袋）は除外する
  const shoppingBagQuantities: { name: string; quantity: number }[] = useMemo(() => {
    if (!revenue.shopping_bags) return []
    const quantityByName = new Map<string, number>()
    revenue.shopping_bags.forEach((bag) => {
      // quantity未設定のレガシーデータは1枚として扱う
      quantityByName.set(bag.name, (quantityByName.get(bag.name) ?? 0) + (bag.quantity ?? 1))
    })
    return Array.from(quantityByName.entries())
      .filter(([, quantity]) => quantity > 0)
      .map(([name, quantity]) => ({ name, quantity }))
  }, [revenue.shopping_bags])

  // 商品と注文種別で注文情報をまとめる
  const itemsByBreadAndType = useMemo(() => {
    const items: CheckoutItemType[] = []
    for (let i = 0; i < revenue.items.length; i = (i + 1) | 0) {
      const item = revenue.items[i]
      const index = items.findIndex((_item) => _item.bread_name === item.bread_name && _item.type === item.type)
      if (index === -1) {
        items.push(item)
      } else {
        const prev = items[index]
        items[index] = {
          ...prev,
          quantity: prev.quantity + item.quantity,
        }
      }
    }
    return items
  }, [revenue.items])

  return (
    <RevenueAccordion
      expandStatus={expandStatus}
      basketNumber={revenue.basket_number}
      resetExpandedStatus={resetExpandedStatus}
      price={revenue.amount}
      status={revenue.status}
    >
      <AccordionItem>
        {itemsByBreadAndType.map((item) => {
          if (item.quantity === 0) return
          return (
            <div className={styles.revenue__item} key={item.checkout_item_ref?.id}>
              <span>{item.bread_name}</span>
              <div className={styles.revenue__item__unit}>
                {item.type === 'daily' && <LightTag color={'blue'} label={'当日'} />}
                {item.type === 'preorder' && <LightTag color={'green'} label={'事前予約'} />}
                {item.type === 'delivery' && <LightTag color={'orange'} label={'取り寄せ'} />}
                <Spacer size={4} />
                <span>{item.quantity}個</span>
              </div>
            </div>
          )
        })}
        {shoppingBagQuantities.map(({ name, quantity }) => {
          return (
            <div className={styles.revenue__item} key={name}>
              <span>{name}</span>
              <FlexRow justifyContent={'start'}>
                <span>{quantity}枚</span>
              </FlexRow>
            </div>
          )
        })}
        {revenue.discount_amount !== 0 && (
          <div className={styles.revenue__item}>
            <span>クーポン利用</span>
            <FlexRow justifyContent={'start'}>
              <span>-{revenue.discount_amount.toLocaleString()}円</span>
            </FlexRow>
          </div>
        )}
        {revenue.delivery_fee_amount !== 0 && (
          <div className={styles.revenue__item}>
            <span>送料</span>
            <FlexRow justifyContent={'start'}>
              <span>{revenue.delivery_fee_amount.toLocaleString()}円</span>
            </FlexRow>
          </div>
        )}
        {revenue.frozen_fee_amount !== 0 && (
          <div className={styles.revenue__item}>
            <span>クール便料金</span>
            <FlexRow justifyContent={'start'}>
              <span>{revenue.frozen_fee_amount.toLocaleString()}円</span>
            </FlexRow>
          </div>
        )}
      </AccordionItem>
    </RevenueAccordion>
  )
}

export default RevenueListItem
